import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import "@/App.css";
import { useAuth } from "@/contexts/AuthContext";
import MetaTags from "@/components/SEO/MetaTags";
import BrandLogo from "@/components/brand/BrandLogo";
import UserAccountMenu from "@/components/brand/UserAccountMenu";
import UserAvatar, { userDisplayName } from "@/components/brand/UserAvatar";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

import {
  Check,
  Globe,
  Activity,
  Users,
  BarChart3,
  Clock,
  Shield,
  Zap,
  ChevronRight,
  ArrowRight,
  Search,
  FileText,
  MapPin,
  Building2,
  CheckCircle2,
  Hospital,
  Stethoscope,
  Calculator,
  Menu,
  X,
  User,
  LogOut,
  MessageCircle,
  Dna,
  ClipboardList,
  FlaskConical,
  FileCheck,
  Users2,
} from "lucide-react";

import axios from "axios";
import FeatureTabsShowcase from "@/components/FeatureTabsShowcase";
import HowItWorksStepper from "@/components/HowItWorksStepper";
import UseCaseCardStack from "@/components/UseCaseCardStack";
import AnimatedFeatureGrid from "@/components/AnimatedFeatureGrid";
import { PUBLIC_PLANS, getPlanById } from "@/utils/payments/subscriptionPlans";
import { startSubscriptionCheckout } from "@/utils/payments/subscribe";
import PriceTag from "@/components/ui/PriceTag";
import { toast } from "@/hooks/use-toast";
import { waitForEntitlement } from "@/utils/subscription";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.getbyonco.com';
const API = `${BACKEND_URL}/api`;

// Trusted Hospitals Data
const trustedHospitals = [
  // India
  { name: "Tata Memorial Centre", city: "Mumbai", country: "India", region: "India" },
  { name: "AIIMS Delhi (Dr. B.R.A. IRCH)", city: "New Delhi", country: "India", region: "India" },
  { name: "Apollo Cancer Centres", city: "Chennai", country: "India", region: "India" },
  { name: "Fortis Healthcare", city: "Mulund", country: "India", region: "India" },
  { name: "HCG Cancer Centre", city: "Bengaluru", country: "India", region: "India" },
  { name: "Kokilaben Dhirubhai Ambani Hospital", city: "Mumbai", country: "India", region: "India" },
  
  // USA
  { name: "Memorial Sloan Kettering Cancer Center", city: "New York", country: "USA", region: "USA" },
  { name: "MD Anderson Cancer Center", city: "Houston", country: "USA", region: "USA" },
  { name: "Mayo Clinic Cancer Center", city: "Rochester", country: "USA", region: "USA" },
  { name: "Dana-Farber Cancer Institute", city: "Boston", country: "USA", region: "USA" },
  { name: "Johns Hopkins Sidney Kimmel Cancer Center", city: "Baltimore", country: "USA", region: "USA" },
  { name: "Cedars-Sinai Cancer Center", city: "Los Angeles", country: "USA", region: "USA" },
  
  // Europe / Canada
  { name: "The Royal Marsden Hospital", city: "London", country: "UK", region: "Europe" },
  { name: "Gustave Roussy Cancer Campus", city: "Paris", country: "France", region: "Europe" },
  { name: "Charité Universitätsmedizin Berlin", city: "Berlin", country: "Germany", region: "Europe" },
  { name: "Princess Margaret Cancer Centre", city: "Toronto", country: "Canada", region: "Europe" },
  
  // Asia-Pacific / Middle East
  { name: "National Cancer Centre Singapore", city: "Singapore", country: "Singapore", region: "Asia-Pacific / Middle East" },
  { name: "National Cancer Center Hospital", city: "Tokyo", country: "Japan", region: "Asia-Pacific / Middle East" },
  { name: "Bumrungrad International Hospital", city: "Bangkok", country: "Thailand", region: "Asia-Pacific / Middle East" },
  { name: "Mediclinic City Hospital", city: "Dubai", country: "UAE", region: "Asia-Pacific / Middle East" },
];

const MedTourismLanding = () => {
  const navigate = useNavigate();   // ✅ Added navigation hook (needed for onClick)
  const { isAuthenticated, user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const [showContactForm, setShowContactForm] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitStatus, setSubmitStatus] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [catalogStats, setCatalogStats] = useState({
    hospitals: null,
    rareCancers: null,
    cities: null,
  });

  const handleSubscribe = async (planId) => {
    const plan = getPlanById(planId);
    if (!plan || paymentLoading) return;

    if (!isAuthenticated) {
      navigate(`/authentication?redirect=${encodeURIComponent('/platform#pricing')}`);
      return;
    }

    setPaymentLoading(true);
    try {
      await startSubscriptionCheckout({
        planType: plan.planType || 'monthly',
        user,
        onSuccess: async ({ entitlement } = {}) => {
          const active = entitlement || await waitForEntitlement(user);
          setPaymentLoading(false);
          toast({
            variant: "success",
            title: active ? "Payment successful" : "Payment received",
            description: active
              ? "Your subscription is now active."
              : "Access activates as soon as the bank confirms. Refresh in a moment if needed.",
          });
          if (active) {
            setTimeout(() => navigate('/app'), 1200);
          }
        },
        onDismiss: () => {
          setPaymentLoading(false);
          toast({
            variant: "info",
            title: "Payment cancelled",
            description: "You can subscribe anytime.",
          });
        },
        onFail: (error) => {
          setPaymentLoading(false);
          toast({
            variant: "error",
            title: "Payment failed",
            description: error.message || "Please try again.",
          });
        }
      });
    } catch (error) {
      setPaymentLoading(false);
      toast({
        variant: "error",
        title: "Payment failed",
        description: error.message || "Failed to initiate payment. Please try again.",
      });
    }
  };

  // Honest catalog counts: never invent fallbacks if /api/stats fails
  useEffect(() => {
    axios.get(`${API}/stats`).then((res) => {
      setCatalogStats({
        hospitals: res.data.hospitals_mapped ?? null,
        rareCancers: res.data.rare_cancers ?? null,
        cities: res.data.cities_covered ?? null,
      });
    }).catch(() => {});
  }, []);

  // scroll-in animations
  useEffect(() => {
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-in");
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
    });

    document.querySelectorAll(".fade-in-section").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = "This field is required.";
    }
    if (!formData.email.trim()) {
      errors.email = "This field is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address.";
    }
    if (!formData.phone.trim()) {
      errors.phone = "This field is required.";
    }
    if (!formData.message.trim()) {
      errors.message = "This field is required.";
    }
    
    setFormErrors(errors);
    
    // If there are errors, don't submit
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setSubmitStatus("loading");
    
    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        message: formData.message.trim()
      }, {
        timeout: 60000, // 60 second timeout
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data && (response.data.status === "success" || response.data.message)) {
        setSubmitStatus("success");
        setFormErrors({});
        // Don't clear form or close modal immediately - show success message
      } else {
        throw new Error("Unexpected response from server");
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      console.error("Error response:", error.response);
      console.error("Error status:", error.response?.status);
      console.error("Error data:", error.response?.data);
      
      if (error.response) {
        // Server responded with error
        const status = error.response.status;
        const detail = error.response.data?.detail || error.response.data?.message;
        
        if (status === 404) {
          setSubmitStatus("error");
          setFormErrors({ 
            submit: "This feature is currently being updated. We're adding improvements to serve you better. Please try again later." 
          });
        } else if (status === 500) {
          setSubmitStatus("error");
          setFormErrors({ 
            submit: "Our service is temporarily unavailable. Please try again in a few moments." 
          });
        } else {
          setSubmitStatus("error");
          setFormErrors({ 
            submit: "We're having trouble processing your request. Please try again in a moment." 
          });
        }
      } else if (error.request) {
        // Request was made but no response received
        setSubmitStatus("error");
        setFormErrors({ 
          submit: "We cannot reach our servers at the moment. Please check your internet connection and try again." 
        });
      } else {
        // Error setting up request
        setSubmitStatus("error");
        setFormErrors({ 
          submit: "We're having trouble connecting right now. Please try again in a moment." 
        });
      }
    }
  };

  const patientFeatures = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Hospital Matching That Fits Your Case",
      description:
        "We match on disease type, location, visa needs, and language from verified centre profiles. Clear case filters, not magical ML.",
              color: "from-sage-mist to-mint-veil",
            },
            {
              icon: <Globe className="w-8 h-8" />,
              title: "All-Inclusive Medical Packages",
              description:
                "Planning packages with itemized estimate bands for treatment share, lodging, transfers, and a care coordinator. These are planning figures, not live hospital quotes.",
              color: "from-keylime-wash to-sage-mist",
            },
            {
              icon: <Clock className="w-8 h-8" />,
              title: "Rapid Second Opinions",
              description:
                "Case review in minutes with a clear brief for clinic, plus a path to book a specialist video consult when you want a human opinion",
              color: "from-slate-hush to-mint-veil",
            },
            {
              icon: <Stethoscope className="w-8 h-8" />,
              title: "Personalized Cancer Treatment Options",
              description:
                "Case-aware educational themes by cancer type, stage, biomarkers, and goals: clinic questions, trials awareness, and next steps you can take into the visit.",
              color: "from-mint-veil to-keylime-wash",
            },
            {
              icon: <Dna className="w-8 h-8" />,
              title: "Genetic and Genomic Testing Opportunities",
              description:
                "Access cutting-edge genetic and genomic testing to identify targeted therapies and personalized treatment approaches",
              color: "from-sage-mist to-slate-hush",
            },
            {
              icon: <ClipboardList className="w-8 h-8" />,
              title: "Symptom Tracking and Management",
              description:
                "Log common symptoms on a 0 to 10 scale with optional notes, interference, personal trends, and a clinic visit note. Entries stay private to your account and are not clinical advice.",
              color: "from-keylime-wash to-mint-veil",
            },
            {
              icon: <FlaskConical className="w-8 h-8" />,
              title: "Access to Latest Innovation and Clinical Trials",
              description:
                "Discover and access the latest clinical trials, innovative treatments, and breakthrough therapies matched to your condition",
              color: "from-mint-veil to-sage-mist",
            },
            {
              icon: <FileCheck className="w-8 h-8" />,
              title: "Health Records You Can Understand",
              description:
                "Upload reports, track reading status, and open a plain-language explanation in chat when the file is ready",
              color: "from-slate-hush to-keylime-wash",
            },
            {
              icon: <Users2 className="w-8 h-8" />,
              title: "A Peer Community",
              description:
                "Connect with others on similar journeys, share experiences, and find support in a safe, moderated community",
              color: "from-sage-mist to-keylime-wash",
    },
  ];

  const howItWorksSteps = [
    {
      title: "Tell Us Your Needs",
      desc: "Cancer type, city, insurance, and optional treatment or language preferences.",
    },
    {
      title: "Match hospitals instantly",
      desc: "Explainable directory fit scores against verified centres, with NCI-designated options first when available.",
    },
    {
      title: "Build a care package",
      desc: "Value, Balanced, and Comfort estimate bands with lodging, travel, and coordinator handoff.",
    },
    {
      title: "Request & get written numbers",
      desc: "Upload reports, request the package, and a coordinator returns hospital-confirmed figures within 48 hours.",
    },
  ];

  const serviceItems = [
    {
      id: "hospitals",
      tab: "Find Hospitals",
      title: "Find Hospitals",
      description: "Browse verified oncology centres worldwide, then refine with Hospital matching.",
      icon: <Hospital className="w-8 h-8" />,
      route: "/find-hospitals",
    },
    {
      id: "match",
      tab: "Hospital Matching",
      title: "Hospital matching",
      description: "Ranked shortlist with fit reasons for cancer type, city, treatments, languages, and traveling patients.",
      icon: <Search className="w-8 h-8" />,
      route: "/get-matched",
    },
    {
      id: "cost",
      tab: "Cost Calculator",
      title: "Cost Calculator",
      description: "USA cost table in USD for surgery, chemo, radiation, and imaging, so families can plan the next conversation.",
      icon: <Calculator className="w-8 h-8" />,
      route: "/cost-calculator",
    },
    {
      id: "rare",
      tab: "Rare Cancers",
      title: "Rare Cancers",
      description: catalogStats.rareCancers != null
        ? `Specialized care for ${catalogStats.rareCancers} rare and ultra-rare cancer types`
        : "Specialized care for rare and ultra-rare cancer types",
      icon: <Activity className="w-8 h-8" />,
      route: "/rare-cancers",
    },
    {
      id: "opinion",
      tab: "Second Opinion",
      title: "Second Opinion",
      description: "Second-opinion brief in minutes, plus a path to book a specialist video consult. Human review timing depends on availability.",
      icon: <FileText className="w-8 h-8" />,
      route: "/second-opinion",
    },
    {
      id: "tele",
      tab: "Teleconsultation",
      title: "Teleconsultation",
      description: "Secure video with US oncologists from our specialist catalog, at centers such as Memorial Sloan Kettering, St. Jude, and Dana-Farber.",
      icon: <Stethoscope className="w-8 h-8" />,
      route: "/teleconsultation",
    },
    {
      id: "tourism",
      tab: "Care Packages",
      title: "All-inclusive care packages",
      description: "Value / Balanced / Comfort US NCI journeys with OOP, lodging, travel bands, and a 48-hour coordinator quote.",
      icon: <Globe className="w-8 h-8" />,
      route: "/journey-builder",
    },
  ];

  const rareUseCases = [
    {
      id: "ultra",
      label: "Ultra-Rare Cancers",
      title: "Ultra-Rare Cancers",
      description: "14 ultra-rare types including DIPG, ATRT, NUT carcinoma. Pediatric tumors, rare carcinomas & aggressive sarcomas.",
      stat: 14,
      statLabel: "types covered",
      icon: <Dna className="w-4 h-4" />,
    },
    {
      id: "very",
      label: "Very Rare Cancers",
      title: "Very Rare Cancers",
      description: "12 high-risk types including Chordoma, Merkel Cell & Angiosarcoma.",
      stat: 12,
      statLabel: "types covered",
      icon: <FlaskConical className="w-4 h-4" />,
    },
    {
      id: "rare",
      label: "Rare Cancers",
      title: "Rare Cancers",
      description: "13 moderately complex types including GIST & Mesothelioma.",
      stat: 13,
      statLabel: "types covered",
      icon: <Activity className="w-4 h-4" />,
    },
  ];

  const hospitalFeatures = [
    {
      icon: <Users className="w-10 h-10" />,
      title: "Qualified International Referrals",
      description:
        "Receive pre-screened international patients with complete case documentation and verified medical records",
      stat: "1,000+ families connected",
    },
  ];

  const stats = [
    { value: "18 to 4", label: "Weeks to first specialist" },
    { value: "160+", label: "Cancer centers & hospital networks" },
    { value: "93%", label: "Families who felt clearer" },
    { value: "60+", label: "NCI-designated centers" },
  ];

  const journeySteps = [
    { n: "1", label: "Your Doctor", tone: "gold" },
    { n: "2", label: "OncoAgent", tone: "teal" },
    { n: "3", label: "ByOnco", tone: "gold" },
    { n: "4", label: "Treatment", tone: "green" },
  ];

  const plans = PUBLIC_PLANS.map((plan) => ({
    ...plan,
    cta: paymentLoading ? 'Opening checkout…' : (plan.cta || 'Subscribe'),
  }));

  return (
    <div className="landing-page">
      <MetaTags
        title="Cancer care marketplace"
        description="Find cancer hospitals, estimate treatment cost, and get a second opinion on the ByOnco marketplace."
        path="/platform"
      />
      {/* Navigation */}
      <nav className="nav-bar">
        <div className="nav-container">
          <div className="nav-logo">
            <BrandLogo className="logo-text" size="md" />
          </div>
          <div className="nav-links">
            <a href="#services" className="nav-link">
              Services
            </a>
            <a href="#features" className="nav-link">
              Features
            </a>
            <a href="#pricing" className="nav-link">
              Pricing
            </a>
            <a href="/about" className="nav-link" onClick={(e) => { e.preventDefault(); navigate('/about'); }}>
              About
            </a>
            {isAuthenticated ? (
              <UserAccountMenu />
            ) : (
              <div className="flex items-center gap-2.5 sm:gap-3 ml-1 sm:ml-2">
                <button
                  onClick={() => navigate("/authentication")}
                  className="nav-login-button"
                  aria-label="Login to ByOnco"
                >
                  Login
                </button>
                <Button
                  onClick={() => {
                    if (isAuthenticated) {
                      navigate("/get-started");
                    } else {
                      navigate("/authentication?redirect=/get-started");
                    }
                  }}
                  className="cta-button"
                  data-testid="nav-contact-button"
                >
                  Get Started <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-forest-ink p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {/* Mobile Menu */}
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden bg-cream-paper border-t border-border-mist"
          >
            <div className="flex flex-col p-4 space-y-4">
              <a
                href="#services"
                className="nav-link-mobile text-charcoal hover:text-forest-ink py-2"
                onClick={() => setMenuOpen(false)}
              >
                Services
              </a>
              <a
                href="#features"
                className="nav-link-mobile text-charcoal hover:text-forest-ink py-2"
                onClick={() => setMenuOpen(false)}
              >
                Features
              </a>
              <a
                href="#pricing"
                className="nav-link-mobile text-charcoal hover:text-forest-ink py-2"
                onClick={() => setMenuOpen(false)}
              >
                Pricing
              </a>
              <a
                href="#about"
                className="nav-link-mobile text-charcoal hover:text-forest-ink py-2"
                onClick={() => setMenuOpen(false)}
              >
                About
              </a>
              {isAuthenticated ? (
                <>
                  <div className="flex items-center gap-3 py-3 border-t border-border-mist mt-2">
                    <UserAvatar user={user} size="lg" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-forest-ink truncate">
                        {userDisplayName(user)}
                      </p>
                      <p className="text-xs text-charcoal truncate">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      navigate("/app");
                      setMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-forest-ink hover:bg-keylime-wash rounded-[7px] transition-colors"
                  >
                    Get Started
                  </button>
                  <button
                    onClick={() => {
                      navigate("/talk");
                      setMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-forest-ink hover:bg-keylime-wash rounded-[7px] transition-colors"
                  >
                    Talk
                  </button>
                  <button
                    onClick={() => {
                      navigate("/profile");
                      setMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-forest-ink hover:bg-keylime-wash rounded-[7px] transition-colors flex items-center gap-2"
                  >
                    <User className="w-4 h-4" />
                    My Profile
                  </button>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-forest-ink hover:bg-mint-veil rounded-[7px] transition-colors flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-2.5 pt-3 border-t border-border-mist mt-1">
                  <button
                    onClick={() => {
                      navigate("/authentication");
                      setMenuOpen(false);
                    }}
                    className="nav-login-button-mobile"
                    aria-label="Login to ByOnco"
                  >
                    Login
                  </button>
                  <Button
                    onClick={() => {
                      if (isAuthenticated) {
                        navigate("/get-started");
                      } else {
                        navigate("/authentication?redirect=/get-started");
                      }
                      setMenuOpen(false);
                    }}
                    className="cta-button-mobile w-full"
                  >
                    Get Started <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section: cinematic video background */}
      <section className="hero-section hero-section--video">
        <div className="hero-video-wrap" aria-hidden="true">
          <video
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
          >
            <source src="/herolandingvideo.mp4" type="video/mp4" />
          </video>
          <div className="hero-video-scrim" />
        </div>

        <motion.div
          className="hero-content-wrapper"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            <h1 className="hero-title" data-testid="hero-title">
              We get cancer patients to specialists
              <br />
              <span className="hero-title-accent">98 days earlier.</span>
            </h1>
            <p className="hero-subtitle" data-testid="hero-subtitle">
              When your doctor suspects cancer, patients lose weeks navigating alone.
              OncoAgent routes them to the right oncologist in days; ByOnco guides them
              through what comes next at US NCI-designated centers.
            </p>
            <div className="hero-cta">
              <Button
                size="lg"
                className="primary-cta"
                onClick={() => navigate(isAuthenticated ? "/get-started" : "/authentication?redirect=/get-started")}
                data-testid="hero-cta-primary"
              >
                For Patients: Get Started <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="secondary-cta"
                onClick={() => navigate("/oncoagent")}
                data-testid="hero-cta-secondary"
              >
                For Doctors: Explore OncoAgent
              </Button>
            </div>
          </motion.div>

          <motion.div
            className="hero-visual"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            <div className="hero-journey-card">
              <p className="hero-journey-kicker">The Patient Journey</p>
              <ol className="hero-journey-steps">
                {journeySteps.map((step, index) => (
                  <li key={step.label} className={`hero-journey-step hero-journey-step--${step.tone}`}>
                    {index > 0 ? <span className="hero-journey-line" aria-hidden="true" /> : null}
                    <span className="hero-journey-num">{step.n}</span>
                    <span className="hero-journey-label">{step.label}</span>
                  </li>
                ))}
              </ol>
              <p className="hero-journey-caption">
                From first suspicion to treatment: one navigator, zero confusion.
              </p>
            </div>
          </motion.div>
        </motion.div>

        <div className="hero-inline-stats" aria-label="ByOnco outcomes">
          {stats.map((stat, index) => (
            <div key={stat.label} className="hero-inline-stat" data-testid={`stat-${index}`}>
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Trusted Hospitals Section */}
      <section className="hospitals-logos-section fade-in-section py-12 md:py-16">
        <motion.div
          className="hospitals-logos-container"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-center text-2xl md:text-3xl lg:text-4xl font-light text-forest-ink">
            Navigate to America&apos;s Leading Cancer Centers
          </h2>
          
          <p className="mt-4 max-w-3xl mx-auto text-center text-base md:text-lg text-charcoal leading-relaxed">
            ByOnco maps more than 60 NCI-designated cancer centers, from MD Anderson and Memorial Sloan Kettering to Mayo Clinic, Dana-Farber and Johns Hopkins, and matches your case to the specialists and care packages that fit it.
          </p>
          <p className="hospitals-logos-disclaimer">
            ByOnco is an independent navigation service. Centers named are for reference and are not affiliated with or endorsing ByOnco.
          </p>
        </motion.div>
      </section>

      {/* Services Navigation Section */}
      <section id="services" className="services-section fade-in-section" style={{ marginTop: "4rem", marginBottom: "4rem" }}>
        <div className="section-header">
          <Badge className="section-badge">Our Services</Badge>
          <h2 className="section-title">
            Complete Cancer Care Solutions
          </h2>
          <p className="section-subtitle">
            Access the tools and services that help you navigate cancer care with clarity
          </p>
        </div>
        <FeatureTabsShowcase
          items={serviceItems}
          onCta={(service) => navigate(service.route)}
        />
      </section>

      <section id="how" className="features-section fade-in-section">
        <HowItWorksStepper
          steps={howItWorksSteps}
          eyebrow="How it works"
          heading="How ByOnco Works"
          lead="From matching to insurance help. Here is how we guide your cancer journey."
        />
      </section>

      {/* Patient Features */}
      <section id="features" className="features-section fade-in-section">
        <div className="section-header">
          <Badge className="section-badge whitespace-nowrap break-normal">For Patients</Badge>
          <h2
            className="section-title break-words"
            data-testid="patient-features-title"
          >
            Your Complete Cancer Care Companion
          </h2>
          <p className="section-subtitle break-words">
            Take control of your cancer care, starting with ByOnco. Understand your diagnosis, explore clinical trials, and track symptoms, all in one place.
          </p>
        </div>
        <AnimatedFeatureGrid features={patientFeatures} />
      </section>

{/* Specialized Care: Rare & Ultra-Rare Cancer */}
<section className="features-section fade-in-section mt-8 md:mt-16">
  <UseCaseCardStack
    eyebrow="Specialized Care"
    heading="Rare & Ultra-Rare Cancer Expertise"
    items={rareUseCases}
  />

  <div className="section-header" style={{ marginTop: "2.5rem", marginBottom: 0, textAlign: "center" }}>
    <p className="section-subtitle break-words" style={{ margin: "0 auto 1.5rem" }}>
      {catalogStats.rareCancers != null
        ? `We specialize in ${catalogStats.rareCancers} rare cancer types, connecting you with the right specialist centres.`
        : 'We specialize in rare cancer types, connecting you with the right specialist centres.'}
    </p>
    <Button
      className="primary-cta"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.5rem",
      }}
      onClick={() => navigate("/rare-cancers")}
    >
      View All Rare Cancers <ArrowRight size={20} />
    </Button>
  </div>
</section>

{/* International Care: Medical Tourism Packages */}
<section className="features-section fade-in-section mt-12 md:mt-24">
  <div className="section-header">
    <span className="section-badge">
      Care packages
    </span>

    <h2 className="section-title break-words">All-inclusive planning packages</h2>
    <p className="section-subtitle break-words">
      Three US NCI-centre journeys (Value, Balanced, and Comfort) with itemized estimate bands for treatment share, lodging, transfers, and a coordinator handoff. Pair with Hospital matching against ByOnco’s verified directory.
    </p>
  </div>

  {/* MAIN WRAPPER */}
  <div className="flex flex-col md:flex-row gap-4 md:gap-8 mt-6 md:mt-12 px-2 md:px-0 justify-center">
    {/* LEFT BOX: CITIES */}
    <div
      className="med-tourism-card w-full md:w-[48%] rounded-[14px] p-4 md:p-6 lg:p-10"
    >
      <div className="flex items-center gap-2 md:gap-3 flex-wrap">
        <MapPin size={22} className="md:w-6 md:h-6 flex-shrink-0 text-forest-ink" />
        <h3 className="feature-title text-base md:text-lg lg:text-xl xl:text-[1.5rem] break-words text-forest-ink font-light">US NCI destinations + global directory</h3>
      </div>

      <p className="feature-description text-sm md:text-base mt-2 md:mt-3 break-words text-charcoal">
        Package estimates focus on leading US oncology centres. Hospital matching also covers verified profiles worldwide for comparison and travel planning.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3 lg:gap-5 mt-4 md:mt-6 lg:mt-8">
        {[
          { name: "Houston", country: "USA", facilities: "MD Anderson & Texas Children’s" },
          { name: "New York City", country: "USA", facilities: "MSK & major NCI centres" },
          { name: "Boston", country: "USA", facilities: "Dana-Farber / Brigham" },
          { name: "Rochester", country: "USA", facilities: "Mayo Clinic" },
          { name: "Baltimore", country: "USA", facilities: "Johns Hopkins" },
          { name: "Los Angeles", country: "USA", facilities: "City of Hope & UCLA" },
          { name: "Mumbai", country: "India", facilities: "Directory match + travel logistics" },
          { name: "London", country: "UK", facilities: "Directory match + travel logistics" },
        ].map((city, idx) => (
          <div
            key={idx}
            className="city-card p-2 md:p-3 lg:p-5 rounded-[14px] text-center"
          >
            <div className="text-sm md:text-base lg:text-lg font-normal text-forest-ink break-words">
              {city.name}
            </div>
            <div className="text-xs md:text-sm mt-1 md:mt-2 text-charcoal font-normal break-words">
              {city.country}
            </div>
            <div className="text-xs md:text-sm lg:text-base mt-1 md:mt-2 text-charcoal break-words">
              {city.facilities}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 md:mt-4 lg:mt-6 text-center flex flex-wrap justify-center gap-2">
        <Button
          className="primary-cta"
          onClick={() => navigate("/journey-builder")}
        >
          Build a care package <ArrowRight size={18} />
        </Button>
        <Button
          variant="outline"
          className="rounded-full border-hair"
          onClick={() => navigate("/get-matched")}
        >
          Match hospitals
        </Button>
      </div>
    </div>

    {/* RIGHT BOX: PACKAGE INCLUSIONS */}
    <div
      className="med-tourism-card blue w-full md:w-[48%] rounded-[14px] p-4 md:p-6 lg:p-10"
    >
      <div className="flex items-center gap-2 md:gap-3 flex-wrap">
        <Building2 size={22} className="md:w-6 md:h-6 flex-shrink-0 text-forest-ink" />
        <h3 className="feature-title text-base md:text-lg lg:text-xl xl:text-[1.5rem] break-words text-forest-ink font-light">What Value / Balanced / Comfort include</h3>
      </div>

      <p className="feature-description text-sm md:text-base mt-2 md:mt-3 break-words text-charcoal">
        Estimate bands grounded in published sources. Then a coordinator confirms written numbers with the centre.
      </p>

      <ul className="mt-4 md:mt-6 lg:mt-8 pl-0 space-y-2 md:space-y-3 lg:space-y-4">
        {[
          "Three ranked NCI-centre journeys with OOP + travel + lodging bands",
          "Insurance framing (employer PPO, Medicare, Medicaid, uninsured)",
          "Hospitality housing screen (Hope Lodge / campus options)",
          "Records transfer and prior-auth checklist",
          "Dedicated coordinator handoff within 48 hours of request",
          "Optional add-ons: airport transfer, second caregiver, concierge",
          "Upload pathology / imaging with your package request",
          "Hospital matching with explainable fit scores",
          "Links to second opinion and video consult",
          "Disclaimer-clear estimates, not live hospital quotes",
        ].map((item, idx) => (
          <li
            key={idx}
            className="flex items-start md:items-center gap-2 md:gap-3 text-sm md:text-base lg:text-lg text-forest-ink break-words"
          >
            <CheckCircle2 size={18} className="md:w-5 md:h-5 flex-shrink-0 mt-0.5 md:mt-0 text-forest-ink" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  </div> {/* ← FIXED: closes main wrapper */}

</section>


      {/* Hospital Features */}
      <section className="hospitals-section fade-in-section">
        <div className="section-header">
          <Badge className="section-badge">For Hospitals</Badge>
          <h2
            className="section-title break-words"
            data-testid="hospital-features-title"
          >
            Grow Your International Patient Base
          </h2>
          <p className="section-subtitle break-words">
            Connect with qualified patients globally and optimize your care delivery
          </p>
        </div>
        <div className="hospitals-grid">
          {hospitalFeatures.map((feature, index) => (
            <Card
              key={index}
              className="hospital-card"
              data-testid={`hospital-feature-${index}`}
            >
              <CardHeader>
                <div className="hospital-icon">{feature.icon}</div>
              </CardHeader>
              <CardContent>
                <CardTitle className="hospital-card-title">
                  {feature.title}
                </CardTitle>
                <CardDescription className="hospital-card-description">
                  {feature.description}
                </CardDescription>
                <div className="hospital-stat">
                  <Shield className="w-5 h-5 text-forest-ink" />
                  <span>{feature.stat}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="pricing-section fade-in-section">
        <div className="section-header">
          <Badge className="section-badge">Pricing</Badge>
          <h2 className="section-title break-words" data-testid="pricing-title">
            Simple, Transparent Pricing
          </h2>
          <p className="section-subtitle break-words">
            Choose the plan that fits your needs
          </p>
        </div>
        <div className="pricing-grid">
          {plans.map((plan, index) => (
            <Card
              key={index}
              className={`pricing-card ${plan.popular ? "popular" : ""}`}
              data-testid={`pricing-plan-${index}`}
            >
              {plan.popular && (
                <div className="popular-badge">Most Popular</div>
              )}
              <CardHeader>
                <CardTitle className="plan-name">{plan.name}</CardTitle>
                <CardDescription className="plan-subtitle">
                  {plan.subtitle}
                </CardDescription>
                <div className="plan-price">
                  <PriceTag
                    currencySymbol={plan.currency === 'INR' ? '₹' : '$'}
                    amount={plan.amount}
                    periodText={plan.period}
                  />
                </div>
                <p className="plan-description">{plan.description}</p>
              </CardHeader>
              <CardContent className="plan-features">
                {plan.features.map((feature, fIndex) => (
                  <div
                    key={fIndex}
                    className="feature-item"
                    data-testid={`plan-${index}-feature-${fIndex}`}
                  >
                    <Check className="w-5 h-5 text-forest-ink flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </CardContent>
              <CardFooter>
                <Button
                  className={`plan-cta ${plan.popular ? "popular" : ""}`}
                  disabled={paymentLoading}
                  onClick={() => handleSubscribe(plan.id)}
                  data-testid={`plan-${index}-cta`}
                >
                  {plan.cta}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section fade-in-section">
        <div className="team-container">
          <h3 className="team-title">Built by a world-class team</h3>
          <div className="team-logos">
            <div className="team-logo-item">
              <img
                src="/logos/meta.svg"
                alt="Meta"
              />
            </div>
            <div className="team-logo-item">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg"
                alt="Amazon"
              />
            </div>
            <div className="team-logo-item">
              <img
                src="/logos/microsoft.svg"
                alt="Microsoft"
              />
            </div>
          </div>
          <p className="team-tagline">
            Built by people who understand your pain, powered by people who can
            solve it
          </p>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="faqs-section fade-in-section">
        <div className="faqs-container">
          <div className="section-header">
            <Badge className="section-badge">FAQs</Badge>
            <h2 className="section-title">Frequently Asked Questions</h2>
          </div>
          <div className="faqs-grid">
            <details className="faq-item">
              <summary className="faq-question">
                How does ByOnco help with rare and ultra-rare cancers?
              </summary>
              <div className="faq-answer">
                ByOnco specializes in matching patients with rare and ultra-rare
                cancers to the right treatment centers globally. We review
                your specific cancer type, treatment requirements, and matches
                you with hospitals in India, US, EU, and Middle East that have
                expertise in treating your condition.
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">
                What is included in the all-inclusive medical packages?
              </summary>
              <div className="faq-answer">
                Packages are planning estimates: treatment share bands, lodging, transfers, and a coordinator handoff.
                They are not live hospital quotes. After you request a package, a coordinator confirms insurance and
                appointments, then returns written numbers from the centre, typically within two business days.
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">
                How fast can I get a second opinion?
              </summary>
              <div className="faq-answer">
                Upload a report or describe your case and get an Second-opinion brief in minutes.
                When you want a clinician to weigh in, book a specialist video consult from the same flow.
                Timing for a live specialist depends on availability. We do not promise a fixed clock for human review.
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">
                What is the ByOnco PRO subscription?
              </summary>
              <div className="faq-answer">
                ByOnco PRO at $19.99/month gives you unlimited access to hospital
                matching against verified US centre profiles, insurance-aware cost framing, fast second
                opinions, and dedicated care coordination.
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">
                How does the Hospital SaaS work?
              </summary>
              <div className="faq-answer">
                Our Hospital SaaS ($10,000/month) provides cancer
                centers with qualified international patient referrals, complete
                case documentation, patient
                conversion tracking, and revenue forecasting tools to grow their
                international patient base.
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">
                Do you help with visa and insurance?
              </summary>
              <div className="faq-answer">
                Yes! We assist with visa applications for medical tourism, help
                identify insurance coverage options, match you with government
                schemes and NGO subsidies, and provide complete financial
                planning for your treatment journey.
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="about" className="final-cta-section fade-in-section">
        <div className="final-cta-glow" />
        <motion.div
          className="final-cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="final-cta-title"
            data-testid="final-cta-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Ready to Navigate Your Cancer Care Journey?
          </motion.h2>
          <motion.p
            className="final-cta-subtitle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Join 10,000+ families who found hope, clarity, and faster treatment
            starts with ByOnco
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              size="lg"
              className="final-cta-button"
              onClick={() => {
                if (isAuthenticated) {
                  navigate("/get-started");
                } else {
                  navigate("/authentication?redirect=/get-started");
                }
              }}
              data-testid="final-cta-button"
            >
              Get Started Today <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section footer-main">
            <div className="footer-logo">
              <BrandLogo className="logo-text" size="md" as="span" />
            </div>
            <p className="footer-description">
              A cancer care marketplace helping patients find the
              right treatment, faster.
            </p>
            <div className="footer-address">
              <p className="footer-address-title">
                PraesidioCare Private Limited
              </p>
              <p className="footer-address-text">Bengaluru, Karnataka, India</p>
              <p className="footer-address-text">
                Email: care@getbyonco.com
              </p>
            </div>
            <div className="built-in-india">
              <span className="india-flag">🇮🇳</span>
              <span>Built in India</span>
            </div>
          </div>

          <div className="footer-section">
            <h4 className="footer-heading">Services</h4>
            <a href="/find-hospitals" className="footer-link" onClick={(e) => { e.preventDefault(); navigate("/find-hospitals"); }}>
              Find Hospitals
            </a>
            <a href="/cost-calculator" className="footer-link" onClick={(e) => { e.preventDefault(); navigate("/cost-calculator"); }}>
              Cost Calculator
            </a>
            <a href="/rare-cancers" className="footer-link" onClick={(e) => { e.preventDefault(); navigate("/rare-cancers"); }}>
              Rare Cancers
            </a>
            <a href="/app" className="footer-link" onClick={(e) => { e.preventDefault(); navigate("/app"); }}>
              Talk to ByOnco AI
            </a>
            <a href="/teleconsultation" className="footer-link" onClick={(e) => { e.preventDefault(); navigate("/teleconsultation"); }}>
              Teleconsultation
            </a>
            <a href="/oncoagent" className="footer-link" style={{ color: '#2ee5b5', fontWeight: 600 }} onClick={(e) => { e.preventDefault(); navigate("/oncoagent"); }}>
              For Doctors: OncoAgent ↗
            </a>
          </div>

          <div className="footer-section">
            <h4 className="footer-heading">Company</h4>
            <a href="/about" className="footer-link" onClick={(e) => { e.preventDefault(); navigate('/about'); }}>
              About
            </a>
            <a href="/careers" className="footer-link" onClick={(e) => { e.preventDefault(); navigate("/careers"); }}>
              Careers
            </a>
            <a href="/get-started" className="footer-link" onClick={(e) => { e.preventDefault(); navigate("/get-started"); }}>
              Get Started
            </a>
          </div>

          <div className="footer-section">
            <h4 className="footer-heading">Legal</h4>
            <a href="/privacy" className="footer-link" onClick={(e) => { e.preventDefault(); navigate("/privacy"); }}>
              Privacy Policy
            </a>
            <a href="/terms-and-conditions" className="footer-link" onClick={(e) => { e.preventDefault(); navigate("/terms-and-conditions"); }}>
              Terms of Service
            </a>
            <a href="/security" className="footer-link" onClick={(e) => { e.preventDefault(); navigate("/security"); }}>
              Security
            </a>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="footer-emergency">
            In a US emergency call 911; for crisis support call or text 988.
          </p>
          <p>
            © 2026 ByOnco by PraesidioCare Private Limited. All rights
            reserved.
          </p>
        </div>
      </footer>

      {/* WhatsApp Floating Chat Button */}
      <a
        href="https://wa.me/919022792824?text=Hi,%20I%20need%20help%20with%20cancer%20treatment%20options."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#20BA5A] text-cream-paper rounded-full p-4  hover:shadow-[#25D366]/50 transition-all duration-300 hover:scale-110 group"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="absolute -top-2 -right-2 bg-red-500 text-forest-ink text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
          1
        </span>
        <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 bg-forest-ink text-cream-paper px-4 py-2 rounded-[14px] opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-sm">
          Chat with us on WhatsApp
        </div>
      </a>

      {/* Contact Modal */}
      {showContactForm && (
        <div
          className="modal-overlay"
          onClick={() => setShowContactForm(false)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">Get Started with ByOnco</h3>
              <button
                className="modal-close"
                onClick={() => setShowContactForm(false)}
                data-testid="modal-close-button"
              >
                ×
              </button>
            </div>
            <form
              onSubmit={handleContactSubmit}
              className="contact-form"
            >
              {submitStatus === "success" ? (
                <div className="text-center py-6 sm:py-8">
                  <CheckCircle2 className="w-12 h-12 sm:w-16 sm:h-16 text-green-500 mx-auto mb-3 sm:mb-4" />
                  <p className="text-base sm:text-lg font-normal text-forest-ink mb-2">
                    Thank you for connecting with us!
                  </p>
                  <p className="text-xs sm:text-sm text-charcoal mb-4 sm:mb-6 px-2">
                    Our team will contact you soon. Thanks for connecting with ByOnco.
                  </p>
                  <Button
                    onClick={() => {
                      setShowContactForm(false);
                      setSubmitStatus("");
                      setFormData({ name: "", email: "", phone: "", message: "" });
                      setFormErrors({});
                    }}
                    className="bg-forest-ink hover:bg-forest-shadow text-cream-paper px-4 py-2 sm:px-6 sm:py-3 text-sm sm:text-base rounded-[14px]"
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="name">Full Name</label>
                    <input
                      id="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        if (formErrors.name) {
                          setFormErrors({ ...formErrors, name: "" });
                        }
                      }}
                      placeholder="Enter your name"
                      data-testid="contact-name-input"
                    />
                    {formErrors.name && (
                      <p className="mt-1 text-xs text-red-400">{formErrors.name}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        if (formErrors.email) {
                          setFormErrors({ ...formErrors, email: "" });
                        }
                      }}
                      placeholder="your@email.com"
                      data-testid="contact-email-input"
                    />
                    {formErrors.email && (
                      <p className="mt-1 text-xs text-red-400">{formErrors.email}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      id="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => {
                        setFormData({ ...formData, phone: e.target.value });
                        if (formErrors.phone) {
                          setFormErrors({ ...formErrors, phone: "" });
                        }
                      }}
                      placeholder="+1 (555) 123-4567"
                      data-testid="contact-phone-input"
                    />
                    {formErrors.phone && (
                      <p className="mt-1 text-xs text-red-400">{formErrors.phone}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      required
                      rows="4"
                      value={formData.message}
                      onChange={(e) => {
                        setFormData({ ...formData, message: e.target.value });
                        if (formErrors.message) {
                          setFormErrors({ ...formErrors, message: "" });
                        }
                      }}
                      placeholder="Tell us about your needs..."
                      data-testid="contact-message-input"
                    />
                    {formErrors.message && (
                      <p className="mt-1 text-xs text-red-400">{formErrors.message}</p>
                    )}
                  </div>

                  {submitStatus === "error" && formErrors.submit && (
                    <div
                      className="alert error"
                      data-testid="contact-error-message"
                    >
                      {formErrors.submit}
                    </div>
                  )}
                  
                  {submitStatus === "loading" && (
                    <div className="text-center py-4">
                      <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-forest-ink border-r-transparent"></div>
                      <p className="text-charcoal mt-2">Submitting...</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="form-submit"
                    disabled={submitStatus === "success" || submitStatus === "loading"}
                    data-testid="contact-submit-button"
                  >
                    {submitStatus === "loading" ? "Sending..." : "Send Message"}
                  </Button>
                </>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedTourismLanding;
